import { isApiError } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Sheet, SheetContent, SheetTrigger, Skeleton, toast } from "@sagot/ui";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { usePermissions, useRouteAgentId } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useWidget } from "../../deploy-knowledge/widget-studio/use-widget-studio";
import { effectiveAudiences, firstOpenAudience, parseAudienceParam, viewerAudiences } from "../shared/audience-presets";
import { agentGreeting } from "../shared/greeting";
import { useAgentConfig, useAudienceLabels, usePlaygroundConfig } from "../shared/queries";
import { UpgradeCard } from "../shared/upgrade-card";
import { ChatPane } from "./chat-pane";
import { ConfigPanel } from "./config-panel";
import { TracePanel } from "./trace-panel";
import { usePlaygroundChat } from "./use-playground-chat";

const NOT_FOUND = 404;
const PAYMENT_REQUIRED = "plan_limit_reached";
/** Code the chat stream gives an HTTP 403: the server refuses an audience the agent is closed to. */
const FORBIDDEN = "http_403";
const FLUSH = "-mx-4 -my-6 h-[calc(100vh-3.5rem)] lg:-mx-8 lg:-my-8";

function PlaygroundSkeleton() {
  return (
    <div className={`${FLUSH} flex min-h-0`} aria-hidden="true">
      <div className="hidden w-72 space-y-4 border-r border-line p-4 lg:block">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}</div>
      <div className="flex-1 space-y-4 p-6"><Skeleton className="mx-auto h-24 max-w-md" /></div>
      <div className="hidden w-96 space-y-3 border-l border-line p-4 xl:block">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
    </div>
  );
}

export function PlaygroundPage() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const agentId = useRouteAgentId() ?? "";
  const config = usePlaygroundConfig(agentId);
  const permissions = usePermissions(agentId);
  const canEdit = !permissions.readOnly && permissions.can("agents.edit", { agentId });
  // Editable instructions come from the full config, which needs agents.view (editors have it).
  const full = useAgentConfig(canEdit ? agentId : null);
  const audienceLabel = useAudienceLabels();
  // "Test as guest / signed-in" in Agent Settings > Knowledge opens this page with ?audience=1|3.
  const [params] = useSearchParams();
  const requested = parseAudienceParam(params.get("audience"));
  // The page stays mounted when the agent in the URL changes, so a pick belongs to the agent it was made for.
  const [picked, setPicked] = useState<{ agentId: string; audience: number } | null>(null);
  const audience = picked?.agentId === agentId ? picked.audience : (requested ?? firstOpenAudience(config.data?.audiences ?? []));
  const chat = usePlaygroundChat(agentId, audience);
  // Welcome message + starters live in the agent's widget config; without widget access the default greeting shows.
  const widget = useWidget(agentId);

  if (config.isPending || (canEdit && full.isPending)) return <PlaygroundSkeleton />;
  if (config.isError) {
    const missing = isApiError(config.error) && config.error.status === NOT_FOUND;
    return missing ? (
      <EmptyState icon="bot" title={t("build.noAgent")} description={t("build.noAgentBody")} action={<Button asChild><AppLink href={consoleRoutes.agents(summary.slug)}>{t("build.goToAgents")}</AppLink></Button>} />
    ) : <ErrorState title={t("build.loadError")} description={t("build.loadErrorBody")} onRetry={() => void config.refetch()} retryLabel={t("common.retry")} />;
  }
  const agent = config.data;
  const instructions = full.data?.instructions ?? null;
  const modelName = agent.model_display_name;
  const greeting = agentGreeting(widget.data, { assistantName: agent.name, orgName: summary.name, languages: agent.languages });
  const subtitle = t("build.playground.chat.subtitle", { model: modelName ?? t("build.playground.chat.defaultModel"), audience: audienceLabel(audience), count: agent.sources_count });
  const configPanel = <ConfigPanel key={`${agent.updated_at}:${String(instructions !== null)}`} config={agent} instructions={instructions} audience={audience} audienceLabel={audienceLabel} onAudience={(next) => { setPicked({ agentId, audience: next }); }} canEdit={canEdit && instructions !== null} />;
  const tracePanel = <TracePanel messageId={chat.messageId} busy={chat.isBusy} context={{ audience_simulated: audience, model_selected: modelName }} />;
  const planGated = chat.error?.code === PAYMENT_REQUIRED;
  const closedToAudience = effectiveAudiences(viewerAudiences(audience), agent.audiences).length === 0;
  const refusedAudience = closedToAudience && chat.error?.code === FORBIDDEN;
  return (
    <div className={`${FLUSH} flex min-h-0`}>
      <aside className="hidden w-72 shrink-0 flex-col border-r border-line bg-surface lg:flex" aria-label={t("build.playground.config.title")}>{configPanel}</aside>
      <ChatPane
        agentName={agent.name}
        subtitle={subtitle}
        greeting={greeting.message}
        starters={greeting.starters}
        messages={chat.messages}
        busy={chat.isBusy}
        error={planGated ? null : chat.error}
        errorText={refusedAudience ? t("build.playground.chat.audienceClosedError", { audience: audienceLabel(audience) }) : undefined}
        onSend={chat.send}
        onStop={chat.stop}
        onRetry={chat.retry}
        onClear={() => { chat.reset(); toast.info(t("build.playground.chat.cleared")); }}
        configTrigger={
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" iconOnly className="lg:hidden" aria-label={t("build.playground.config.open")}><Icon name="sliders-horizontal" /></Button>
            </SheetTrigger>
            <SheetContent title={t("build.playground.config.sheetTitle")} closeLabel={t("common.close")} className="max-w-xs">{configPanel}</SheetContent>
          </Sheet>
        }
        traceTrigger={
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="secondary" size="sm" className="xl:hidden"><Icon name="activity" />{t("build.playground.chat.showTrace")}</Button>
            </SheetTrigger>
            <SheetContent title={t("build.playground.trace.sheetTitle")} closeLabel={t("common.close")}>{tracePanel}</SheetContent>
          </Sheet>
        }
      />
      {planGated ? <div className="absolute inset-x-4 bottom-28 mx-auto max-w-2xl"><UpgradeCard feature={t("build.playground.chat.planTitle")} /></div> : null}
      <aside className="hidden w-96 shrink-0 flex-col border-l border-line bg-surface xl:flex" aria-label={t("build.playground.trace.title")}>{tracePanel}</aside>
    </div>
  );
}
